import React, { useRef } from "react";
import { inject, observer } from "mobx-react";
import LabelVideoStore from "store/labelVideoStore";
import { formatTime } from "../utils/formatTime";
import IncidentEditor, {
  INCIDENT_LIST_MARGIN_TOP,
  INCIDENT_ROW_GAP,
  getIncidentRowHeight,
} from "./incidentEditor";
import { theme } from "./theme";

const TIMELINE_HEIGHT = 50;
const LABELS_COLUMN_WIDTH = 140;

type Props = {
  onFrameChange: (frameIndex: number) => void;
  labelVideoStore?: LabelVideoStore;
  showLabels?: boolean;
};

const ProgressBar = ({
  onFrameChange,
  labelVideoStore,
  showLabels = true,
}: Props) => {
  const progressBarRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const [hoverInfo, setHoverInfo] = React.useState<{
    time: string;
    frame: number;
  } | null>(null);
  const [mousePosition, setMousePosition] = React.useState<{
    x: number;
    y: number;
  } | null>(null);

  const duration = Math.floor((labelVideoStore?.totalFrames || 0) / (labelVideoStore?.fps || 1))
  const stepsBig = Math.floor(duration / 10); // Marqueurs tous les 10s
  const stepsSmall = Math.floor(duration / 1); // Marqueurs tous les 10s

  // HANDLERS ///////////////////////////////

  const handleMouseDown = (e: React.MouseEvent) => {
    isDraggingRef.current = true;
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!progressBarRef.current) return;

    const rect = progressBarRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const frameIndex = Math.floor(
      (x / rect.width) * (labelVideoStore?.totalFrames || 0)
    );

    if (isDraggingRef.current) {
      onFrameChange(frameIndex);
    }

    setHoverInfo({
      time: formatTime(frameIndex, labelVideoStore?.fps || 0),
      frame: frameIndex,
    });
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => {
    isDraggingRef.current = false;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  const handleMouseLeave = () => {
    setHoverInfo(null);
    setMousePosition(null);
  };

  const handleClick = (e: any) => {
    labelVideoStore?.setClickedIncident(null);
    const rect = progressBarRef.current?.getBoundingClientRect();
    if (rect) {
      const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
      const frameIndex = Math.floor(
        (x / rect.width) * (labelVideoStore?.totalFrames || 0)
      );
      onFrameChange(frameIndex);
    }
  }

  //////////////////////////////////////////////////////

  const classList = labelVideoStore?.classList ?? [];
  const hasPredictions =
    (labelVideoStore?.predictionIncidents?.length ?? 0) > 0;
  const rowHeight = getIncidentRowHeight(hasPredictions);

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        backgroundColor: "#f3f3f3",
        borderTop: "1px solid #cfcfcf",
        height: "101%",
        paddingLeft: "10px",
        paddingRight: "10px",
        display: "flex",
        flexDirection: "row",
      }}
    >
      {showLabels && (
        <div
          style={{
            width: LABELS_COLUMN_WIDTH,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            borderRight: "1px solid #cfcfcf",
            marginRight: "8px",
            userSelect: "none",
          }}
        >
          <div style={{ height: TIMELINE_HEIGHT }} />
          <div
            style={{
              marginTop: `${INCIDENT_LIST_MARGIN_TOP}px`,
              display: "flex",
              flexDirection: "column",
              gap: `${INCIDENT_ROW_GAP}px`,
            }}
          >
            {classList.map((incidentClass) => {
              const classMeta = labelVideoStore?.getClassMeta(incidentClass);
              const classColor =
                classMeta?.color || theme.colors.background;
              return (
                <div
                  key={`label-${incidentClass}`}
                  title={incidentClass}
                  style={{
                    height: rowHeight,
                    display: "flex",
                    alignItems: "center",
                    paddingLeft: 8,
                    paddingRight: 8,
                    borderLeft: `3px solid ${classColor}`,
                    fontSize: "0.8em",
                    fontWeight: "bold",                    
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {incidentClass}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div
        style={{ flex: 1, position: "relative", cursor: "pointer" }}
        onClick={handleClick}
      >
      <div
        style={{
          width: "100%",
          height: `${TIMELINE_HEIGHT}px`,
          position: "relative",
          cursor: "pointer",
        }}
        ref={progressBarRef}
        // onClick={handleClick}
        onMouseMove={(e: React.MouseEvent<HTMLDivElement>) =>
          handleMouseMove(e.nativeEvent)
        }
        onMouseLeave={handleMouseLeave}
      >
        
        {/* MARKERS ****************************************/}
        <div style={{ width: "100%"}}>
          {/* BIG MARKERS */}
          {Array.from({ length: stepsBig + 1 }).map((_, i) => {
            const seconds = i * 10;
            const minutes = Math.floor(seconds / 60);
            const remainingSeconds = seconds % 60;

            const textPosition = 
              (i == 0)
              ? -3 
              : (i === stepsBig)
              ? 30 + 3
              : 15

            return (
              <>
              <div
                key={i}
                style={{
                  position: 'absolute',
                  top: 10,
                  left: `${(seconds * 100) / (duration)}%`,
                  width: 1,
                  height: 16,
                  backgroundColor: '#b7b7b7',
                  transform: 'translateX(-1px)',
                }}
              />
              <div
                key={i}
                style={{
                  position: 'absolute',
                  top: 10 + 16 + 3,
                  fontSize: "0.7em",
                  color: "#a0a0a0",
                  left: `calc(${(i * 10 * 100) / (duration)}% - ${textPosition}px)`,
                  width: 40,
                  height: 16,                  
                  transform: 'translateX(-1px)',
                }}
              >
                {`${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`}
              </div>
            </>
            )
          })}

          {/* SMALL MARKERS */}
          {Array.from({ length: stepsSmall + 1 }).map((_, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                top: 14,
                left: `${(i * 100) / (duration)}%`,
                width: 1,
                height: 8,
                backgroundColor: '#b7b7b7',
                transform: 'translateX(-1px)',
              }}
            />
          ))}
        </div>


        {/* VERTICAL LINE *********************/}
        <div
          id="progress-bar-current-frame"
          style={{
            position: "absolute",
            left: `${((labelVideoStore?.currentFrame || 0) /
              (labelVideoStore?.totalFrames || 1)) *
              100}%`,
            top: "6px", // 17px
            background: "#2948FF",
            width: "1px",
            height: "calc(100vh)",
            cursor: "grab",
            zIndex: 10,
          }}
          onMouseDown={handleMouseDown}
        >
          <div
            style={{
              position: "absolute",
              left: "-4.5px",
              top: -10 +5,
              width: 10,
              height: 10,
              backgroundColor: "#2948FF",
              borderTopLeftRadius: "2px",
              borderTopRightRadius: "2px"
            }}
          />
          <div
            style={{
              position: "absolute",
              left: "-4.5px",
              top: 0 +5,
              width: 0,
              height: 0,
              borderLeft: "5px solid transparent",
              borderRight: "5px solid transparent",
              borderTop: "5px solid #2948FF",
            }}
          />
        </div>
      </div>

      {/* TOOLTIP */}
      {hoverInfo && mousePosition && progressBarRef.current && (
        <div
          style={{
            position: "fixed",
            left: mousePosition.x - 40,
            top: progressBarRef.current?.getBoundingClientRect().top - 40,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            color: "white",
            padding: "4px 8px",
            borderRadius: "4px",
            fontSize: "12px",
            zIndex: 1000,
          }}
        >
          Frame: {hoverInfo.frame} | Temps: {hoverInfo.time}
        </div>
      )}
      <IncidentEditor
        clientWidth={progressBarRef.current?.clientWidth || 0}
        onFrameChange={onFrameChange}
        handleDragBar={handleMouseDown}
        showLabels={showLabels}
      />
      </div>
    </div>
  );
};

export default inject("labelVideoStore")(observer(ProgressBar));
